import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import type { PostAuthDestination } from '@/hooks/usePostAuthRoute';

/**
 * Render a redirect that may leave the current clinic's URL namespace.
 *
 * `<Navigate>` alone cannot do this: the router's `basename` is fixed at mount,
 * so a path aimed at another clinic would resolve under the current prefix. A
 * cross-namespace hop is a full page load instead — which is also what we want
 * for correctness, since the whole app (auth store, query cache, basename) has
 * to be rebuilt for the new clinic.
 *
 * Rendering `null` during the hard case is deliberate: `location.replace` is not
 * instantaneous, and painting the destination's chrome in the old namespace for
 * a frame would be worse than a blank one.
 */
export function Redirect({ to }: { to: PostAuthDestination }) {
  const href = to.kind === 'hard' ? to.href : null;
  useEffect(() => {
    if (href !== null) window.location.replace(href);
  }, [href]);

  if (to.kind === 'router') return <Navigate to={to.to} replace />;
  return null;
}
